import type { ComponentType } from "react";

import { AboutPage } from "./pages/company/AboutPage";
import { ContactPage } from "./pages/company/ContactPage";
import { SupportPage } from "./pages/company/SupportPage";
import { WhatsAppPage } from "./pages/company/WhatsAppPage";
import { HomePage } from "./pages/HomePage";
import { CookiePolicyPage } from "./pages/legal/CookiePolicyPage";
import { DataDeletionPage } from "./pages/legal/DataDeletionPage";
import { PrivacyPage } from "./pages/legal/PrivacyPage";
import { RefundPolicyPage } from "./pages/legal/RefundPolicyPage";
import { TermsPage } from "./pages/legal/TermsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PaymentSuccessPage } from "./pages/PaymentSuccessPage";

export type Route = {
  path: string;
  title: string;
  description: string;
  Page: ComponentType;
  // false: prerendered with noindex and left out of sitemap.xml.
  indexable?: boolean;
};

// Shared by the app (document.title) and scripts/prerender.js, which writes
// one static HTML file per route with these as its <head> tags.
export const ROUTES: Route[] = [
  {
    path: "/",
    title: "Hethera — Pay bills, buy airtime & data on WhatsApp in Nigeria",
    description:
      "Buy airtime and data, renew DStv, GOtv and StarTimes, and pay for electricity by sending a WhatsApp message. Every payment is confirmed with your PIN and refunded automatically if it fails. Ask for a report of what you spent.",
    Page: HomePage,
  },
  {
    path: "/about",
    title: "About & contact — Hethera",
    description:
      "Hethera is a bill-payment assistant on WhatsApp by Hethera Engineering Ltd (RC 9859449), Lagos. How we look after your money, who we work with and how to reach us.",
    Page: AboutPage,
  },
  {
    path: "/support",
    title: "Email support — Hethera",
    description:
      "Email info@hetheraengineering.com about a payment, refund, card or your Hethera account. What to include so we can help quickly.",
    Page: SupportPage,
  },
  {
    path: "/contact",
    title: "Phone & office — Hethera",
    description:
      "Call Hethera support on +234 811 999 5541, or find our registered office in Surulere, Lagos.",
    Page: ContactPage,
  },
  {
    path: "/whatsapp",
    title: "Chat with Hethera on WhatsApp",
    description:
      "Open Hethera on WhatsApp, set up in a minute and pay your first bill. How to check you are talking to the official account.",
    Page: WhatsAppPage,
  },
  {
    path: "/privacy",
    title: "Privacy Policy — Hethera",
    description:
      "What personal data Hethera collects, how we use it, who we share it with and your rights under the Nigeria Data Protection Act 2023.",
    Page: PrivacyPage,
  },
  {
    path: "/terms",
    title: "Terms of Service — Hethera",
    description:
      "The terms for using Hethera to buy airtime, data, cable TV and electricity on WhatsApp, operated by Hethera Engineering Ltd.",
    Page: TermsPage,
  },
  {
    path: "/refund-policy",
    title: "Refund Policy — Hethera",
    description:
      "If you pay through Hethera and your airtime, data, cable or electricity purchase is not delivered, your card is refunded automatically.",
    Page: RefundPolicyPage,
  },
  {
    path: "/cookies",
    title: "Cookie Policy — Hethera",
    description:
      "Hethera's websites set no advertising or analytics cookies. What little they store in your browser, and why.",
    Page: CookiePolicyPage,
  },
  {
    path: "/data-deletion",
    title: "Data deletion — Hethera",
    description:
      "How to delete your Hethera account and the personal data we hold about you.",
    Page: DataDeletionPage,
  },
  {
    // Paystack's redirect after a card is added (backend PAYSTACK_CALLBACK_URL).
    path: "/payment-success",
    title: "Payment successful — Hethera",
    description:
      "Your payment is being confirmed. You will be taken back to WhatsApp.",
    Page: PaymentSuccessPage,
    indexable: false,
  },
];

export const NOT_FOUND: Route = {
  path: "/404",
  title: "Page not found — Hethera",
  description: "This page does not exist.",
  Page: NotFoundPage,
};

export function findRoute(pathname: string): Route {
  const trimmed = pathname.replace(/\/+$/, "").toLowerCase() || "/";
  return ROUTES.find((r) => r.path === trimmed) ?? NOT_FOUND;
}
